<template>
	<div class="place-editor row">
		<!-- Province -->
		<div class="col-sm-6">
			<div class="form-group">
				<label
					class="font-weight-bold"
					v-if="isPickup">
					Pickup Location Province
				</label>
				<label
					class="font-weight-bold"
					v-if="isDrop">
					Drop Location Province
				</label>
				<multiselect
					placeholder="Select Province"
					label="province_name"
					track-by="province_id"
					v-model="province"
					:options="provinces"
					:show-labels="false"
					:allow-empty="false"
					:loading="isFetchingProvinces"
					:preselect-first="true"
					:disabled="provinces.length === 0"
					@select="onProvinceChange">
				</multiselect>
			</div>
		</div>

		<!-- City -->
		<div class="col-sm-6">
			<div class="form-group">
				<label
					class="font-weight-bold"
					v-if="isPickup">
					Pickup Location City
				</label>
				<label
					class="font-weight-bold"
					v-if="isDrop">
					Drop Location City
				</label>
				<multiselect
					placeholder="Select City"
					label="city_name_with_type"
					track-by="city_id"
					v-model="city"
					:options="cities"
					:show-labels="false"
					:allow-empty="false"
					:loading="isFetchingCities"
					:preselect-first="true"
					:disabled="cities.length === 0"
					@select="onCityChange">
				</multiselect>
			</div>
		</div>

		<!-- Subdistrict -->
		<div class="col-sm-6">
			<div class="form-group">
				<label
					class="font-weight-bold"
					v-if="isPickup">
					Pickup Location Subdistrict
				</label>
				<label
					class="font-weight-bold"
					v-if="isDrop">
					Drop Location Subdistrict
				</label>
				<multiselect
					placeholder="Select Subdistrict"
					label="subdistrict_name"
					track-by="subdistrict_id"
					v-model="subdistrict"
					:options="subdistricts"
					:show-labels="false"
					:allow-empty="false"
					:loading="isFetchingSubdistricts"
					:preselect-first="false"
					:disabled="subdistricts.length === 0"
					@select="onSubdistrictChange">
				</multiselect>
			</div>
		</div>

		<!-- ZIP Code -->
		<div class="col-sm-6">
			<div class="form-group">
				<label class="font-weight-bold">ZIP Code</label>
				<input
					type="text"
					class="form-control"
					:value="zip"
					@change="onZipChange"
				>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import shippingApi from '@/api/shipping';

export default {
	name: 'PlaceEditor',
	props: {
		provinceId: {
			type: Number,
			default: 0,
		},
		cityId: {
			type: Number,
			default: 0,
		},
		subdistrictId: {
			type: Number,
			default: 0,
		},
		zip: {
			type: Number,
			default: 0,
		},
		provinceName: {
			type: String,
			default: '',
		},
		cityName: {
			type: String,
			default: '',
		},
		subdistrictName: {
			type: String,
			default: '',
		},
		courier: {
			type: String,
			default: 'sicepat',
		},
		origin: {
			type: String,
			default: '',
		},
		method: {
			type: String,
			default: 'PICKUP',
		},
	},
	data() {
		return {
			provinces: [],
			province: null,
			cities: [],
			city: null,
			subdistricts: [],
			subdistrict: null,
			isFetchingProvinces: false,
			isFetchingCities: false,
			isFetchingSubdistricts: false,
		};
	},
	methods: {
		fetchProvinces() {
			this.isFetchingProvinces = true;
			const callback = (response) => {
				this.provinces = response.data;
				this.isFetchingProvinces = false;
			};
			const errorCallback = (e) => {
				// eslint-disable-next-line
				console.log(e);
				this.isFetchingProvinces = false;
			};
			shippingApi.getAllProvinces(callback, errorCallback);
		},
		fetchCities(provinceId) {
			this.isFetchingCities = true;
			const params = {
				province_id: provinceId,
				courier: this.courier,
			};
			const callback = (response) => {
				this.isFetchingCities = false;
				this.cities = response.data;
			};
			const errorCallback = (e) => {
				this.isFetchingCities = false;
				// eslint-disable-next-line
				console.log(e);
			};
			shippingApi.getCities(params, callback, errorCallback);
		},
		fetchSubdistricts(cityId) {
			this.isFetchingSubdistricts = true;
			const params = {
				city_id: cityId,
				courier: this.courier,
			};
			const callback = (response) => {
				this.subdistricts = response.data;
				this.isFetchingSubdistricts = false;
			};
			const errorCallback = () => {
				this.isFetchingSubdistricts = false;
			};
			shippingApi.getSubdistrictsWithOrigin(params, callback, errorCallback);
		},
		onProvinceChange(province) {
			this.city = null;
			this.subdistrict = null;
			this.cities = [];
			this.subdistricts = [];

			const provinceId = province ? province.province_id : 0;
			const provinceName = province ? province.province_name : 0;
			this.$emit('update:provinceId', provinceId);
			this.$emit('update:provinceName', provinceName);
			this.$emit('update:cityId', 0);
			this.$emit('update:cityName', '');
			this.$emit('update:subdistrictId', 0);
			this.$emit('update:subdistrictName', '');
			this.fetchCities(provinceId);
		},
		onCityChange(city) {
			this.subdistrict = null;
			this.subdistricts = [];

			const cityId = city ? city.city_id : 0;
			const cityName = city ? city.city_name_with_type : '';
			const origin = city[this.originKey] || '';
			this.$emit('update:cityId', cityId);
			this.$emit('update:cityName', cityName);
			this.$emit('update:subdistrictId', 0);
			this.$emit('update:subdistrictName', '');
			this.$emit('update:origin', origin);
			this.fetchSubdistricts(cityId);
		},
		onSubdistrictChange(subdistrict) {
			const subdistrictId = subdistrict ? subdistrict.subdistrict_id : 0;
			const subdistrictName = subdistrict ? subdistrict.subdistrict_name : '';
			this.$emit('update:subdistrict', subdistrict);
			this.$emit('update:subdistrictId', subdistrictId);
			this.$emit('update:subdistrictName', subdistrictName);
		},
		setDefaultProvince() {
			let provinceId = this.provinceId;
			if (!provinceId && this.store) {
				provinceId = this.store.shipping.origin.province.id;
			}
			const selectedProvince = this.provinces.find(province => province.province_id === provinceId);
			if (selectedProvince) {
				this.province = selectedProvince;
				this.fetchCities(selectedProvince.province_id);
			}
		},
		setDefaultCity() {
			let cityId = this.cityId;
			if (!cityId && this.store) {
				cityId = this.store.shipping.origin.city.id;
			}
			const selectedCity = this.cities.find(city => city.city_id === cityId);
			if (selectedCity) {
				this.city = selectedCity;
				this.fetchSubdistricts(selectedCity.city_id);
			}
		},
		setDefaultSubdistrict() {
			let subdistrictId = this.subdistrictId;
			if (!subdistrictId && this.store) {
				subdistrictId = this.store.shipping.origin.subdistrict.id;
			}
			const selectedSubdistrict = this.subdistricts.find(subdistrict => subdistrict.subdistrict_id === subdistrictId);
			if (selectedSubdistrict) {
				this.subdistrict = selectedSubdistrict;
				this.$emit('update:subdistrict', selectedSubdistrict);
			}
		},
		onZipChange(event) {
			const value = event.target.value.trim();
			const intValue = parseInt(value);
			this.$emit('update:zip', intValue);
		},
		updateOrigin() {
			if (this.city) {
				let origin = '';
				if ((this.courier === 'sap' || this.courier === 'ninja') && this.subdistrict) {
					origin = this.subdistrict[this.originKey] || '';
				} else {
					origin = this.city[this.originKey] || '';
				}
				this.$emit('update:origin', origin);
			}
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			store: 'store',
		}),
		originKey() {
			let originKey = 'origin';
			if (this.courier === 'sicepat') {
				originKey = 'scpt_origin';
			} else if (this.courier === 'jne') {
				originKey = 'jne_origin';
			} else if (this.courier === 'jnt') {
				originKey = 'jnt_pickup_code';
			} else if (this.courier === 'sap') {
				originKey = 'sap_code';
			} else if (this.courier === 'ninja') {
				originKey = 'ninja_origin';
			}
			return originKey;
		},
		isPickup() {
			return this.method === 'PICKUP';
		},
		isDrop() {
			return this.method === 'DROP';
		},
	},
	watch: {
		user() {
			this.setDefaultProvince();
		},
		provinces() {
			this.setDefaultProvince();
		},
		cities() {
			this.setDefaultCity();
		},
		city() {
			this.updateOrigin();
		},
		subdistricts() {
			this.setDefaultSubdistrict();
		},
		subdistrict(subdistrict) {
			if (subdistrict) {
				if (subdistrict.zip && subdistrict.zip !== '') {
					this.$emit('update:zip', subdistrict.zip);
				}
				this.updateOrigin();
				// const origin = subdistrict.origin || '';
				// this.$emit('update:origin', origin);
			}
		},
		courier() {
			this.updateOrigin();
			// if (this.city) {
			// 	const origin = this.city[this.originKey] || '';
			// 	this.$emit('update:origin', origin);
			// }
		},
		provinceId(id) {
			if (id) {
				// this.$emit('update:provinceId', id);
				this.setDefaultProvince();
			}
		},
		cityId(id) {
			if (id) {
				this.setDefaultCity();
			}
		},
		subdistrictId(id) {
			if (id) {
				this.setDefaultSubdistrict();
			}
		},
	},
	created() {
		this.fetchProvinces();
	},
};
</script>
